import type {
  HealthcareService,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { now } from '@seen/core/utils'
import type { SuggestAppointmentSlotsRequest } from '@seen/request-types/suggestAppointmentSlots'
import type { UseQueryResult } from '@tanstack/react-query'
import { addWeeks, max, startOfDay } from 'date-fns'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

// How far out the AI may place slots. Wide enough to honor preferences that
// push a visit weeks out (e.g. onto the participant's day-center days) while
// still bounding model latency and context size.
const SEARCH_HORIZON_WEEKS = 6

export interface AiAppointmentSlot {
  id: string
  period: { start: string; end: string }
  practitioners: Reference<Practitioner>[]
  rank: number
  // Present only when a stated org/practitioner/participant rule or preference
  // drove the slot; omitted for a merely-available time.
  reason?: string
}

export interface AiRecommendedAppointmentSlots {
  slots: AiAppointmentSlot[]
  traceId: string
}

export interface UseAiRecommendedAppointmentSlotsResult {
  query: UseQueryResult<AiRecommendedAppointmentSlots | undefined>
  // Re-runs the suggestion, optionally steering it with free-text guidance.
  // Each distinct feedback produces a fresh generation (new Langfuse trace).
  regenerate: (feedbackText?: string) => void
  // True while a regenerate is in flight (a fetch driven by feedback), so the
  // initial auto-fetch doesn't read as "regenerating".
  isRegenerating: boolean
  // Fire-and-forget thumbs up/down against the current suggestion's trace.
  recordFeedback: (value: 0 | 1, comment?: string) => void
}

export function useAiRecommendedAppointmentSlots({
  patient,
  healthcareService,
  practitioner,
  searchFrom,
  excludeAppointmentId,
}: {
  patient: Reference<Patient> | undefined
  healthcareService: Reference<HealthcareService> | undefined
  practitioner: Reference<Practitioner> | undefined
  // Earliest acceptable appointment date, derived from the calendar's visible
  // horizon. Undefined searches from today.
  searchFrom?: string
  // The visit being moved, dropped from the conflict calendar.
  excludeAppointmentId?: string
}): UseAiRecommendedAppointmentSlotsResult {
  const { ai } = useSeenApi()

  // The current generation's steering guidance (kept pure — this text is what
  // the model reads) and a monotonic regenerate nonce. `regenNonce === 0` is
  // the initial auto-fetch; each regenerate bumps it so React Query refetches
  // even when the guidance text is empty or unchanged.
  const [{ feedback, regenNonce }, setRegen] = useState<{
    feedback: string
    regenNonce: number
  }>({ feedback: '', regenNonce: 0 })
  // Trace of the last successful suggestion, threaded into the next regenerate
  // for lineage and used as the target of thumbs feedback.
  const lastTraceIdRef = useRef<string | undefined>(undefined)

  // Day granularity on purpose: nudging the time within the selected day must
  // not rekey the query, and a date already behind us cannot be booked, so the
  // window never starts before today.
  const { searchStart, searchEnd } = useMemo(() => {
    const today = startOfDay(now())
    const start = max([searchFrom ? startOfDay(searchFrom) : today, today])
    return {
      searchStart: start.toISOString(),
      searchEnd: addWeeks(start, SEARCH_HORIZON_WEEKS).toISOString(),
    }
  }, [searchFrom])

  const result = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.aiRecommendedAppointmentSlots({
      patient,
      healthcareService,
      practitioner,
      start: searchStart,
      end: searchEnd,
      regenNonce,
      excludeAppointmentId,
    }),
    queryFn: async (): Promise<AiRecommendedAppointmentSlots | undefined> => {
      if (
        !ai ||
        !patient?.reference ||
        !healthcareService?.reference ||
        !practitioner?.reference
      ) {
        return undefined
      }

      const request: SuggestAppointmentSlotsRequest = {
        patient: { reference: patient.reference },
        practitioner: { reference: practitioner.reference },
        healthcareService: { reference: healthcareService.reference },
        searchStart,
        searchEnd,
        feedback: feedback || undefined,
        previousTraceId: regenNonce > 0 ? lastTraceIdRef.current : undefined,
      }
      if (excludeAppointmentId) {
        request.excludeAppointmentId = excludeAppointmentId
      }
      const response = await ai.suggestAppointmentSlots(request)

      lastTraceIdRef.current = response.traceId

      const slots: AiAppointmentSlot[] = response.slots
        .map((slot) => ({
          id: `${slot.start}-${slot.end}`,
          period: { start: slot.start, end: slot.end },
          practitioners: [practitioner],
          rank: slot.rank,
          reason: slot.reason,
        }))
        // Best-first: higher rank wins, tie-break on earliest start.
        .sort((a, b) =>
          b.rank !== a.rank
            ? b.rank - a.rank
            : a.period.start.localeCompare(b.period.start)
        )

      return { slots, traceId: response.traceId }
    },
  })

  const regenerate = useCallback((feedbackText?: string) => {
    setRegen((prev) => ({
      feedback: feedbackText?.trim() ?? '',
      regenNonce: prev.regenNonce + 1,
    }))
  }, [])

  const recordFeedback = useCallback(
    (value: 0 | 1, comment?: string) => {
      const traceId = lastTraceIdRef.current
      if (!ai || !traceId) {
        return
      }
      void ai
        .recordSuggestionFeedback({ traceId, value, comment })
        .catch(() => undefined)
    },
    [ai]
  )

  useErrorToast(
    result.error &&
      `Error generating appointment slots: ${result.error.message ?? 'Unknown error'}`
  )

  return {
    query: result,
    regenerate,
    isRegenerating: regenNonce > 0 && result.isFetching,
    recordFeedback,
  }
}
